/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client'

import { type FirebaseApp } from 'firebase/app'
import {
  type FirestoreLocalCache,
  memoryLocalCache,
  memoryLruGarbageCollector,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore'

import { type AuthPersistenceClass } from './auth-persistence'
import {
  memoryCacheFallbackActive,
  sharedCacheLockActive,
} from './firestore-multitab-wedge'
import {
  pruneBrowserSharedClientState,
  type SharedClientStatePrunePlan,
} from './firestore-shared-client-state'

/**
 * The durable cache's size bound: the SDK's own default, 40 MB, written down.
 * See "The durable cache's size" on {@link localCacheFor}.
 */
export const DURABLE_CACHE_SIZE_BYTES = 40 * 1024 * 1024

/**
 * The Firestore `localCache` an origin of this persistence class may use
 * (AGL-1456).
 *
 * ## Why this keys off the *auth* persistence class
 *
 * The name is historical — the class predates this file (AGL-1379) — but the
 * question it answers was never auth-specific. `AuthPersistenceClass` is
 * documented as *"how much of a session an origin is allowed to keep"*, and
 * AGL-1099a's D6 justified `ephemeral` on the grounds that **"the customer can
 * re-point the DNS and read this origin."**
 *
 * That reasoning does not stop at the credential. It is equally true of every
 * document body Firestore caches in IndexedDB on that origin — measured, not
 * argued: the PoC (`docs/design/agl-1099a-poc-findings.md` §5) dumped every
 * object store on the origin and found full document contents under
 * `firestore/<app>/<project>/main → remoteDocumentsV14`, alongside
 * `firestore_clients_*` / `firestore_online_state_*` /
 * `firestore_sequence_number_*` in `localStorage`. D6 hardened the key and
 * left the safe behind it open; this closes it.
 *
 * **One declaration, two consequences — deliberately.** A separate
 * `firestoreCache` prop would let a caller declare a custom console domain
 * `ephemeral` for auth and still hand it a persistent cache, which is exactly
 * the split that produced this bug. There is one fact about the origin, so
 * there is one place to state it.
 *
 * ## The two classes
 *
 * - `durable` → `persistentLocalCache` with the multi-tab manager. Unchanged
 *   from what every host has always run, and it must stay that way:
 *   `*.aglyn.com` / `*.aglyn.app` are ours forever, and this cache is part of
 *   why console read volume is what it is (AGL-1440).
 * - `ephemeral` → `memoryLocalCache`, so **nothing** reaches disk on an origin
 *   whose DNS the customer can re-point at their own server.
 *
 * ## The durable cache's size
 *
 * `cacheSizeBytes` is spelled out at the SDK default so the bound is stated
 * here and cannot quietly become `CACHE_SIZE_UNLIMITED` if that default moves.
 * Stating it changes nothing at runtime, and what it governs is narrower than
 * the name suggests — measured against `@firebase/firestore` 4.17.1:
 *
 * - LRU collection is on without it: an unset `cacheSizeBytes` resolves to
 *   `LruParams.DEFAULT`, the same 40 MB threshold.
 * - It runs only in the tab holding the primary lease, 1 minute after that tab
 *   becomes primary and every 5 minutes after.
 * - The threshold is compared with the byte size of **cached documents**
 *   (`remoteDocumentsV14`) and nothing else. `targets` and `targetDocuments`
 *   rows are collected alongside documents once documents cross it, never
 *   before.
 * - It never touches `localStorage`, where the multi-tab manager strands a
 *   record per tab and per query target; {@link pruneSharedClientStateFor}
 *   handles those.
 *
 * It is deliberately not lowered. A smaller cache evicts exactly the documents
 * that make this cache worth having — a cold load resuming from disk, and
 * listeners painting through a stale session — and a collected target is given
 * a NEW id the next time its query is listened to, which adds a `localStorage`
 * record rather than removing one.
 *
 * ## Why the memory cache gets the LRU collector, not the default
 *
 * `memoryLocalCache()` defaults to `memoryEagerGarbageCollector`, which drops
 * a document the moment no listener references it — so navigating away from a
 * console page and back re-reads its whole working set from the server. The
 * LRU collector keeps documents in the JS heap up to its 40 MB default instead,
 * which recovers most of the intra-session read saving `persistentLocalCache`
 * was giving us.
 *
 * It costs nothing against the property being protected: the LRU collector is
 * still **memory only**. Nothing is written to IndexedDB, nothing survives a
 * tab close, and a page load on a re-pointed origin starts empty — the same
 * bound `inMemoryPersistence` gives the refresh token.
 *
 * The read cost that remains is real and is the stated trade: a **cold** page
 * load on a custom console domain re-reads its working set where a durable
 * origin would have resumed from disk, and multiple tabs no longer share one
 * backend connection through `persistentMultipleTabManager`. That is the right
 * trade for an Enterprise-only feature, but it is a trade.
 *
 * ## `Failed to obtain primary lease for action '…'` is not a bug to fix
 *
 * Exactly one open tab holds the IndexedDB primary lease and applies remote
 * events; the rest read the results it writes. When the lease changes hands —
 * a tab opening, closing, or coming back to the foreground — a transaction
 * already in flight on the losing tab finds itself no longer primary, and the
 * SDK `logError`s that message immediately before throwing a
 * `failed-precondition` that its OWN multi-tab sync engine catches
 * (`ignoreIfPrimaryLeaseLoss`) and downgrades to a debug line. The tab then
 * continues as a secondary. Nothing is dropped and no listener stops.
 *
 * It is therefore noise from a handled handoff, and it scales with how many
 * tabs are churning rather than with anything the console does. The message
 * names a lease, so the obvious reading is that the multi-tab manager is
 * missing and should be added — it is already here, which is why the message
 * appears at all: `persistentSingleTabManager` refuses the second tab's
 * persistence outright instead of sharing it. Confirmed at runtime by the
 * `owner` record in `firestore/<app>/<project>/main`, which carries
 * `allowTabSynchronization: true`.
 */
export function localCacheFor(
  originClass: AuthPersistenceClass,
): FirestoreLocalCache {
  if (originClass === 'ephemeral') {
    return memoryLocalCache({ garbageCollector: memoryLruGarbageCollector() })
  }
  return persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
    cacheSizeBytes: DURABLE_CACHE_SIZE_BYTES,
  })
}

/**
 * The cache class THIS TAB's Firestore runs, which is the origin's own class
 * unless the tab has fallen back to the memory cache (AGL-3428), or another
 * tab has just seen the shared one locked (AGL-4090).
 *
 * A durable origin's tab that found the shared IndexedDB locked by another
 * tab records a fallback in its `sessionStorage` and reloads; this is where
 * that reload picks it up. The answer is `ephemeral` then because the
 * ephemeral cache is exactly what the tab needs — memory only, shared with no
 * other tab — and nothing else keys off this answer: the AUTH persistence of
 * the tab keeps the origin's class, so the session survives the reload.
 *
 * When what that tab found was a LOCK on `databaseName`, it also leaves a
 * short-lived record in `localStorage`, and a tab opened while it is fresh
 * starts on the memory cache here instead of hanging on the same lock until
 * it finds out for itself. Tabs already running keep the durable cache. See
 * `firestore-multitab-wedge.ts`.
 */
export function firestoreCacheClassFor(
  originClass: AuthPersistenceClass,
  databaseName?: string,
  fallbackActive: () => boolean = () =>
    memoryCacheFallbackActive() || sharedCacheLockActive(databaseName),
): AuthPersistenceClass {
  if (originClass === 'durable' && fallbackActive()) return 'ephemeral'
  return originClass
}

/**
 * Prunes the multi-tab records the durable cache strands in `localStorage`
 * (AGL-2845), for an app whose Firestore this page has just initialized.
 *
 * Keyed off the same origin class as {@link localCacheFor}, because the
 * durable class is the only one running `persistentMultipleTabManager`, the
 * only writer of those records. An ephemeral origin's memory cache writes
 * none, so it resolves without looking.
 *
 * Fire-and-forget: it never rejects and nothing waits on it. What it deletes,
 * and why a live tab's records never are, is in
 * `firestore-shared-client-state.ts`.
 */
export function pruneSharedClientStateFor(
  originClass: AuthPersistenceClass,
  app: FirebaseApp,
): Promise<SharedClientStatePrunePlan | undefined> {
  if (originClass === 'ephemeral') return Promise.resolve(undefined)
  return pruneBrowserSharedClientState(app.name, app.options?.projectId)
}
